/**
 * Improves form accessibility by automatically focusing the first input field with a validation error
 * when form submission fails. This helps screen reader users, keyboard-only users, and reduces cognitive
 * load by directing attention to errors immediately.
 *
 * **Key Features:**
 * - Integrates seamlessly with Formik via a compound component pattern (`FormErrorGuide.Control`)
 * - Eliminates manual ref management, reducing boilerplate
 * - Only refocuses when `isSubmitting` is true, avoiding disruptive behavior (e.g., after Formik validation)
 * - Announces error messages to screen readers using an ARIA live region when associated to with a describedby
 *
 * **Why This Matters:**
 * - Meets WCAG 2.1 AA accessibility standards
 * - Reduces support burden and enhances brand reputation
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { Text } from '@betterment/samba-react';
import { ErrorMessage, FormikErrors } from 'formik';

type RefType = HTMLInputElement | HTMLButtonElement | null;

type FormErrorGuideContextType = {
  register: (name: string, ref: RefType) => void;
};

const FormErrorGuideContext = createContext<FormErrorGuideContextType | undefined>(undefined);

export type Props<T> = {
  children: React.ReactNode;
  errors: FormikErrors<T>;
  isSubmitting: boolean;
};

const FormErrorGuide = <T extends Record<string, unknown>>({ children, errors, isSubmitting }: Props<T>) => {
  const inputRef = useRef<Partial<Record<keyof T, RefType>>>({});
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (isSubmitting) {
      hasFocusedRef.current = true;
    }

    if (!hasFocusedRef.current) return;

    const firstErrorKey = Object.keys(errors)[0] as keyof T | undefined;
    if (firstErrorKey && inputRef.current[firstErrorKey]) {
      inputRef.current[firstErrorKey].focus();
      hasFocusedRef.current = false;
    }
  }, [errors, isSubmitting]);

  const register = useCallback(
    (name: keyof T, ref: RefType) => {
      inputRef.current[name] = ref;
    },
    [inputRef]
  );

  return useMemo(
    () => <FormErrorGuideContext.Provider value={{ register }}>{children}</FormErrorGuideContext.Provider>,
    [children, register]
  );
};

type ControlProps = {
  name: string;
  children: (ref: (el: RefType) => void) => React.ReactNode;
};

FormErrorGuide.Control = function FormErrorGuideControl({ name, children }: ControlProps) {
  const context = useContext(FormErrorGuideContext);
  if (!context) throw new Error('FormErrorGuide.Control must be used within FormErrorGuide');
  return <>{children(ref => context.register(name, ref))}</>;
};

export const FormikErrorMessage: React.FC<{ name: string; id?: string }> = ({ name, id }) => {
  return (
    <ErrorMessage name={name}>
      {msg => (
        <Text as="p" color="text.critical" css={{ marginTop: 'space.50' }} role="alert" id={id ?? ''}>
          {msg}
        </Text>
      )}
    </ErrorMessage>
  );
};

export default FormErrorGuide;
